import React, { useEffect, useState } from 'react';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';

const NotificationTemplatesSection = ({ data, onChange, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [localData, setLocalData] = useState(data);
  const [isSaving, setIsSaving] = useState(false);
  const [activeTemplate, setActiveTemplate] = useState('registration');

  useEffect(() => {
    setLocalData(data);
    setIsEditing(false);
    setIsSaving(false);
  }, [data]);

  const templates = [
    {
      id: 'registration',
      name: '참가 신청 확인',
      icon: 'CheckCircle',
      description: '참가자가 세션에 신청했을 때 발송되는 메시지',
      variables: ['{name}', '{session}', '{date}', '{time}']
    },
    {
      id: 'payment',
      name: '결제 안내',
      icon: 'CreditCard',
      description: '결제 정보를 안내하는 메시지',
      variables: ['{name}', '{amount}', '{bank}', '{account}', '{deadline}']
    },
    {
      id: 'confirmation',
      name: '참가 확정',
      icon: 'Check',
      description: '결제 완료 후 참가가 확정되었을 때 발송되는 메시지',
      variables: ['{name}', '{session}', '{date}', '{time}', '{location}']
    },
    {
      id: 'reminder',
      name: '세션 리마인더',
      icon: 'Bell',
      description: '세션 시작 전 발송되는 알림 메시지',
      variables: ['{name}', '{session}', '{date}', '{time}', '{location}']
    },
    {
      id: 'cancellation',
      name: '취소 안내',
      icon: 'XCircle',
      description: '참가가 취소되었을 때 발송되는 메시지',
      variables: ['{name}', '{session}', '{reason}']
    }
  ];

  const handleChange = (templateId, value) => {
    setLocalData(prev => ({
      ...prev,
      [templateId]: value
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    await onSave(localData);
    setIsSaving(false);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setLocalData(data);
    setIsEditing(false);
  };

  const insertVariable = (variable) => {
    const template = templates?.find(t => t?.id === activeTemplate);
    if (template) {
      const currentValue = localData?.[activeTemplate] || '';
      handleChange(activeTemplate, currentValue + variable);
    }
  };

  const activeTemplateData = templates?.find(t => t?.id === activeTemplate);

  return (
    <div className="bg-card rounded-xl border border-border p-4 md:p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-warning/10 rounded-md flex items-center justify-center">
            <Icon name="MessageSquare" size={20} color="var(--color-warning)" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
              알림 템플릿
            </h2>
            <p className="text-sm caption text-muted-foreground">
              자동 발송되는 메시지 템플릿을 관리합니다
            </p>
          </div>
        </div>
        
        {!isEditing && (
          <Button
            variant="outline"
            size="default"
            iconName="Edit"
            iconPosition="left"
            onClick={() => setIsEditing(true)}
          >
            수정
          </Button>
        )}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-2">
          {templates?.map((template) => (
            <button
              key={template?.id}
              onClick={() => setActiveTemplate(template?.id)}
              className={`
                w-full p-4 rounded-md text-left transition-athletic
                ${activeTemplate === template?.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted hover:bg-muted/80 text-foreground'
                }
              `}
            >
              <div className="flex items-center gap-3">
                <Icon 
                  name={template?.icon} 
                  size={20}
                  color={activeTemplate === template?.id ? '#FFFFFF' : 'currentColor'}
                />
                <div className="flex-1">
                  <h3 className="text-sm font-medium">{template?.name}</h3>
                  <p className={`text-xs caption mt-1 ${
                    activeTemplate === template?.id 
                      ? 'text-primary-foreground/80' 
                      : 'text-muted-foreground'
                  }`}>
                    {template?.description}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-2 space-y-4">
          {activeTemplateData && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-heading font-semibold text-foreground">
                  {activeTemplateData?.name}
                </h3>
                {isEditing && (
                  <div className="flex flex-wrap gap-2">
                    {activeTemplateData?.variables?.map((variable) => (
                      <button
                        key={variable}
                        onClick={() => insertVariable(variable)}
                        className="px-3 py-1 text-xs data-text bg-muted hover:bg-muted/80 rounded-md transition-athletic active-press"
                      >
                        {variable}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-foreground">
                  메시지 내용
                </label>
                <textarea
                  value={localData?.[activeTemplate] || ''}
                  onChange={(e) => handleChange(activeTemplate, e?.target?.value)}
                  disabled={!isEditing}
                  rows={8}
                  className="w-full px-4 py-3 bg-background border border-border rounded-md text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                  placeholder="메시지 템플릿을 입력하세요..."
                />
                <p className="text-sm caption text-muted-foreground">
                  변수를 클릭하여 메시지에 삽입할 수 있습니다
                </p>
              </div>

              <div className="bg-muted rounded-md p-4">
                <h4 className="text-sm font-medium text-foreground mb-2">미리보기</h4>
                <div className="bg-card rounded-md p-4 border border-border">
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {localData?.[activeTemplate]?.replace(/{name}/g, '홍길동')?.replace(/{session}/g, '피클볼 오픈 게임')?.replace(/{date}/g, '2026년 1월 15일')?.replace(/{time}/g, '오후 7시')?.replace(/{amount}/g, '20,000원')?.replace(/{bank}/g, 'KB국민은행')?.replace(/{account}/g, '123-456-789012')?.replace(/{deadline}/g, '1월 14일')?.replace(/{location}/g, '서울시 강남구 테헤란로 123')?.replace(/{reason}/g, '개인 사정') || '메시지 내용이 없습니다'}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
      {isEditing && (
        <div className="flex gap-3 pt-6 border-t border-border mt-6">
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
            fullWidth
          >
            취소
          </Button>
          <Button
            variant="default"
            onClick={handleSave}
            loading={isSaving}
            iconName="Save"
            iconPosition="left"
            fullWidth
          >
            저장
          </Button>
        </div>
      )}
    </div>
  );
};

export default NotificationTemplatesSection;